import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import unescapedText from 'I18n/utils/unescapedText';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { range } from '../../utils/range/range';
import styled from 'foundations-theming/utils';
import partial from 'react-utils/partial';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { getTabIndex } from '../../utils/TabIndex';
import { DefaultIconComponent, IconToggle } from '../IconToggle/IconToggle';
import { Controllable } from '../Controllable';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
const StyledAbstractRatingInput = styled('div')(({
  theme
}) => {
  return Object.assign({}, getFocusStyleObject({
    theme
  }));
});
export const InternalAbstractRatingInput = _ref => {
  let {
      IconComponent = DefaultIconComponent,
      disabled = false,
      iconCount = 5,
      tabIndex,
      value,
      readOnly = false,
      showPercentage = false,
      id,
      onHoveredValueChange,
      onChange,
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, ["IconComponent", "disabled", "iconCount", "tabIndex", "value", "readOnly", "showPercentage", "id", "onHoveredValueChange", "onChange", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
  const [focusValue, setFocusValue] = useState(null);
  const onHoveredValueChangeRef = useRef(onHoveredValueChange);
  const isFirstRenderRef = useRef(true);
  useLayoutEffect(() => {
    onHoveredValueChangeRef.current = onHoveredValueChange;
  }, [onHoveredValueChange]);
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      callIfPossible(onHoveredValueChangeRef.current, syntheticEvent(focusValue));
    }
  }, [focusValue]);

  // This should always be the last effect that is run
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
  }, []);
  const getPercentageFilled = index => {
    if (value == null) {
      return 0;
    }
    if (focusValue !== null) {
      // @ts-ignore - 'focusValue' is possibly 'undefined'
      if (index <= focusValue) {
        return 100;
      }
      return 0;
    }
    if (index <= Math.floor(value)) {
      return 100;
    }
    if (index > Math.ceil(value)) {
      return 0;
    }
    return Math.round(value % 1 * 100);
  };
  const getIsHighlighted = index => {
    if (focusValue !== null) {
      // @ts-ignore - 'focusValue' is possibly 'undefined'
      return index <= focusValue;
    }

    // @ts-ignore - 'value' is possibly 'undefined'
    return index <= value;
  };
  const handleChange = newValue => {
    if (disabled || readOnly) {
      return;
    }
    callIfPossible(onChange, syntheticEvent(newValue));
  };
  const handleKeyDown = evt => {
    let nextValue = value || 0;
    if (evt.key === 'ArrowLeft') {
      nextValue = Math.floor(nextValue) <= 0 ? 0 : Math.ceil(nextValue) - 1;
    } else if (evt.key === 'ArrowRight') {
      nextValue = Math.ceil(nextValue) >= iconCount ? iconCount : Math.floor(nextValue) + 1;
    }
    handleChange(nextValue);
  };
  const handleHover = newValue => {
    if (disabled || readOnly) {
      return;
    }
    setFocusValue(newValue);
  };
  return /*#__PURE__*/_jsx(StyledAbstractRatingInput, Object.assign({}, rest, {
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'AbstractRatingInput',
    id: id,
    "aria-disabled": disabled,
    "aria-valuemax": iconCount,
    "aria-valuemin": 0,
    "aria-valuenow": value || 0,
    "aria-valuetext": unescapedText('salesUI.UIIconRating.ariaValueText', {
      value: value || 0,
      maxValue: iconCount
    }),
    role: "slider",
    tabIndex: getTabIndex(disabled, tabIndex),
    onKeyDown: handleKeyDown,
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'AbstractRatingInput',
    children: range(1, iconCount + 1).map(i => /*#__PURE__*/_createElement(IconToggle, Object.assign({}, rest, {
      key: i,
      index: i,
      onChange: partial(handleChange, i),
      onMouseEnter: partial(handleHover, i),
      onMouseLeave: partial(handleHover, null),
      "aria-label": "",
      checked: getIsHighlighted(i),
      readOnly: readOnly,
      percentFilled: showPercentage ? getPercentageFilled(i) : null,
      tabIndex: -1,
      IconComponent: IconComponent,
      active: value === i,
      disabled: disabled,
      focus: i === focusValue
      // @ts-ignore - 'focusValue' is possibly 'null' or 'undefined'
      ,
      hover: i <= focusValue
    })))
  }));
};
export const AbstractRatingInput = Controllable(InternalAbstractRatingInput);
AbstractRatingInput.displayName = 'AbstractRatingInput';